<template>
  <div :id="'app'" :class="[{ 'lao-font': currentLanguage === 'lo' }, platformClass, { 'mobile': isMobile, 'desktop': !isMobile }]">
    <!-- Loading Screen -->
    <div v-if="isLoading" class="loading-screen">
      <div class="loading-content">
        <img src="/logo.png" alt="AKATHRM Logo" class="loading-logo" />
        <div class="loading-spinner"></div>
        <p class="loading-text">{{ t('Loading AKATHRM...') }}</p>
        <p class="loading-subtext">{{ loadingMessage }}</p>
        <p class="platform-indicator">{{ platform.toUpperCase() }}</p>
      </div>
    </div>
    
    <!-- Main App Content -->
    <div v-else>
      <!-- Mobile Header - Hide on welcome page -->
      <div v-if="!isWelcomePage" class="mobile-header">
        <div class="header-content">
          <img src="/logo.png" alt="AKATHRM Logo" class="app-logo" />
          <div class="header-actions">
            
            <!-- Language Selector -->
            <div class="language-selector">
              <el-dropdown @command="handleLanguageChange" trigger="click">
                <button>
                  <span>{{ getCurrentFlag() }}</span>
                  <span class="lang-code-small">{{ currentLanguage.toUpperCase() }}</span>
                  <el-icon><ArrowDown /></el-icon>
                </button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item 
                      v-for="lang in availableLanguages" 
                      :key="lang.code" 
                      :command="lang.code"
                      :class="{ 'active': currentLanguage === lang.code }"
                    >
                      <span>{{ getFlag(lang.code) }}</span>
                      <span>{{ lang.nativeName }}</span>
                      <span>{{ lang.name }}</span>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
              
              <!-- Cache indicator for mobile -->
              <span v-if="isMobile && hasCachedTranslations" class="cache-age-indicator">
                {{ cacheAgeMinutes }}m
              </span>
            </div>
            
            <!-- Notifications -->
            <div class="notification-section">
              <el-dropdown @command="handleNotificationCommand" trigger="click" placement="bottom-end">
                <el-badge :value="unreadCount" :hidden="unreadCount === 0" class="notification-badge" type="info">
                  <button>
                    <el-icon><Bell /></el-icon>
                  </button>
                </el-badge>
                <template #dropdown>
                  <el-dropdown-menu class="notifications-dropdown">
                    <div class="notifications-header">
                      <span class="notifications-title">{{ t('Notifications') }}</span>
                      <div class="notification-actions">
                        <button 
                          @click="refreshNotifications"
                          :disabled="refreshingNotifications"
                          :title="t('Refresh notifications')"
                        >
                          <el-icon><Refresh /></el-icon>
                        </button>
                      </div>
                    </div>
                    
                    <div v-if="recentNotifications && recentNotifications.length > 0" class="notifications-list">
                      <div class="notifications-list-header">
                        <button 
                          v-if="recentNotifications && recentNotifications.length > 0" 
                          @click="markAllAsRead"
                          class="mark-all-read-btn"
                        >
                          <el-icon><Check /></el-icon>
                          {{ t('Mark all as read') }}
                        </button>
                      </div>
                      
                      <div 
                        v-for="notification in recentNotifications.slice(0, 5)" 
                        :key="notification.id"
                        class="notification-item"
                        :class="{ 'unread': !notification.isRead }"
                        @click="handleNotificationClick(notification)"
                      >
                        <el-icon class="notification-icon" :style="{ color: getNotificationColor(notification.type) }">
                          <component :is="getNotificationIcon(notification.type)" />
                        </el-icon>
                        <div class="notification-content">
                          <div class="notification-title">{{ notification.title }}</div>
                          <div class="notification-time">{{ formatTime(notification.createdAt) }}</div>
                        </div>
                        <div class="notification-actions">
                          <button 
                            v-if="!notification.isRead" 
                            @click.stop="markAsRead(notification.id)"
                            class="mark-read-btn"
                          >
                            <el-icon><Check /></el-icon>
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <div v-else class="empty-notifications">
                      <el-icon class="empty-icon"><Bell /></el-icon>
                      <p>{{ t('No notifications') }}</p>
                    </div>
                    
                    <el-dropdown-item divided command="viewAll" class="view-all-item">
                      <el-icon><View /></el-icon>
                      {{ t('View All Notifications') }}
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
              
              <!-- Clear Data Cache Button -->
              <button 
                @click="clearDataCache"
                :title="t('Clear data cache')"
              >
                <el-icon><Delete /></el-icon>
              </button>
            </div>
            
            <div class="auth-actions">
              <el-button 
                v-if="!isAuthenticated" 
                type="primary" 
                size="small" 
                @click="goToLogin"
              >
                Login
              </el-button>
              <el-dropdown v-else @command="handleAuthCommand">
                <button class="user-menu-trigger" type="button">
                  <el-avatar :size="32" :src="user?.profileURL">
                    {{ displayNameInitial }}
                  </el-avatar>
                  <span v-if="displayName" class="user-menu-name">{{ displayName }}</span>
                  <el-icon class="user-menu-caret"><ArrowDown /></el-icon>
                </button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item disabled class="user-menu-header-item">
                      <div class="user-menu-header">
                        <strong>{{ displayName || t('User') }}</strong>
                      </div>
                    </el-dropdown-item>
                    <el-dropdown-item command="profile">
                      <el-icon><User /></el-icon>
                      {{ t('Employee Profile') }}
                    </el-dropdown-item>
                    <el-dropdown-item command="changePassword">
                      <el-icon><Lock /></el-icon>
                      {{ t('Change Password') }}
                    </el-dropdown-item>
                    <el-dropdown-item 
                      v-if="isMobile" 
                      command="nativeSettings"
                      divided
                    >
                      <el-icon><Setting /></el-icon>
                      {{ t('Native Settings') }}
                    </el-dropdown-item>
                    <el-dropdown-item divided command="logout">
                      <el-icon><SwitchButton /></el-icon>
                      {{ t('Logout') }}
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Main Content -->
      <div :class="['main-content', { 'welcome-layout': isWelcomePage, 'mobile-content': isMobile }]">
        <router-view />
      </div>
      
      <!-- Mobile Bottom Navigation - Only show when authenticated and not on welcome page -->
      <nav
        v-if="isAuthenticated && !isWelcomePage && isMobile"
        class="mobile-nav"
        aria-label="Primary"
      >
        <div class="nav-track">
          <div
            class="nav-indicator"
            :class="{ 'is-ready': navIndicatorReady }"
            :style="navIndicatorStyle"
          >
            <div class="nav-indicator-pill">
              <span class="nav-crystal-shine" aria-hidden="true" />
              <span class="nav-crystal-glow" aria-hidden="true" />
            </div>
          </div>
          <button
            v-for="item in mobileNavItems"
            :key="item.path"
            type="button"
            class="nav-item"
            :class="{ active: isNavActive(item.path) }"
            @click="navigateMobileNav(item.path)"
          >
            <span class="nav-item-inner">
              <el-icon class="nav-icon"><component :is="item.icon" /></el-icon>
              <span class="nav-label">{{ t(item.label) }}</span>
            </span>
          </button>
        </div>
      </nav>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { House, Clock, Calendar, UserFilled, User, Bell, Lock, SwitchButton, TrendCharts, Check, Refresh, ArrowDown, Delete, View, InfoFilled, CircleCheckFilled, WarningFilled, CircleCloseFilled, Timer, Setting } from '@element-plus/icons-vue'
import { useTranslation } from '@/utils/translations'
import nativeBridge from './services/nativeBridge'

export default {
  name: 'App',
  components: {
    House,
    Clock,
    Calendar,
    UserFilled,
    User,
    Bell,
    Lock,
    SwitchButton,
    TrendCharts,
    Check,
    Refresh,
    ArrowDown,
    Delete,
    View,
    InfoFilled,
    CircleCheckFilled,
    WarningFilled,
    CircleCloseFilled,
    Timer,
    Setting
  },
  data() {
    return {
      currentTime: '',
      notificationInterval: null,
      isLoading: true,
      loadingMessage: 'Initializing...',
      platform: 'web', // 'web', 'android', 'ios'
      isMobile: false,
      refreshingNotifications: false,
      navIndicatorReady: false,
      mobileNavItems: [
        { path: '/app', label: 'Home', icon: 'House' },
        { path: '/attendance', label: 'Attnd', icon: 'Clock' },
        { path: '/leave', label: 'Leave', icon: 'Calendar' },
        { path: '/ot-management', label: 'OT', icon: 'Timer' },
        { path: '/kpi/list', label: 'KPI', icon: 'TrendCharts' },
        { path: '/recruitment', label: 'Rec', icon: 'User' },
        { path: '/profile', label: 'Prof', icon: 'UserFilled' }
      ]
    }
  },
  computed: {
    ...mapGetters('auth', ['isAuthenticated', 'user', 'displayName']),
    ...mapGetters('notifications', ['unreadCount', 'recentNotifications']),
    ...mapGetters('language', ['currentLanguage', 'availableLanguages', 'hasCachedTranslations', 'cacheAgeMinutes']),
    isWelcomePage() {
      return this.$route.path === '/'
    },
    platformClass() {
      return `platform-${this.platform}`
    },
    displayNameInitial() {
      const name = (this.displayName || '').trim()
      return name ? name.charAt(0).toUpperCase() : 'U'
    },
    activeNavIndex() {
      const path = this.$route.path
      const exact = this.mobileNavItems.findIndex(item => item.path === path)
      if (exact >= 0) return exact
      const soft = this.mobileNavItems.findIndex(item => {
        if (item.path === '/app') return false
        return path === item.path || path.startsWith(item.path + '/') ||
          (item.path === '/kpi/list' && path.startsWith('/kpi'))
      })
      return soft >= 0 ? soft : 0
    },
    navIndicatorStyle() {
      const count = this.mobileNavItems.length || 1
      return {
        width: `calc(100% / ${count})`,
        transform: `translate3d(${this.activeNavIndex * 100}%, 0, 0)`
      }
    }
  },
  methods: {
    ...mapActions('notifications', ['getUnreadCount', 'markAsRead', 'markAllAsRead', 'clearTranslationCache']),
    ...mapActions('auth', ['logout']),
    ...mapActions('language', ['setLanguage', 'loadTranslations', 'refreshTranslations']),

    isNavActive(path) {
      const current = this.$route.path
      if (current === path) return true
      if (path === '/kpi/list' && current.startsWith('/kpi')) return true
      if (path !== '/app' && current.startsWith(path + '/')) return true
      return false
    },

    navigateMobileNav(path) {
      if (this.$route.path === path) return
      this.$router.push(path)
    },
    
    updateCurrentTime() {
      const now = new Date()
      this.currentTime = now.toLocaleTimeString('en-US', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },
    
    detectPlatform() {
      const userAgent = navigator.userAgent.toLowerCase()
      
      // Check if running in Flutter WebView
      if (window.flutterBridge || window.Flutter) {
        this.platform = 'android' // Default to Android for Flutter
        this.isMobile = true
        return
      }
      
      // Check for iOS
      if (/iphone|ipad|ipod/.test(userAgent)) {
        this.platform = 'ios'
        this.isMobile = true
        return
      }
      
      // Check for Android
      if (/android/.test(userAgent)) {
        this.platform = 'android'
        this.isMobile = true
        return
      }
      
      // Check for mobile devices
      if (/mobile|tablet/.test(userAgent) || window.innerWidth <= 768) {
        this.isMobile = true
        // Try to detect platform from other indicators
        if (/webkit/.test(userAgent) && !/chrome/.test(userAgent)) {
          this.platform = 'ios'
        } else {
          this.platform = 'android'
        }
        return
      }
      
      // Default to web
      this.platform = 'web'
      this.isMobile = false
    },
    
    applyPlatformStyles() {
      // Add platform-specific classes to body
      document.body.classList.add(this.platformClass)
      document.body.classList.add(this.isMobile ? 'mobile' : 'desktop')
      
      // Platform-specific adjustments
      if (this.platform === 'ios') {
        this.applyIOSStyles()
      } else if (this.platform === 'android') {
        this.applyAndroidStyles()
      }
    },
    
    applyIOSStyles() {
      // iOS-specific styling — keep small bottom inset for home indicator only
      document.documentElement.style.setProperty('--safe-area-top', 'env(safe-area-inset-top, 0px)')
      document.documentElement.style.setProperty('--safe-area-bottom', 'min(12px, env(safe-area-inset-bottom, 0px))')
      document.documentElement.style.setProperty('--safe-area-left', 'env(safe-area-inset-left, 0px)')
      document.documentElement.style.setProperty('--safe-area-right', 'env(safe-area-inset-right, 0px)')
      
      // iOS-specific behaviors
      this.$nextTick(() => {
        // Prevent zoom on input focus (iOS)
        const inputs = document.querySelectorAll('input, textarea, select')
        inputs.forEach(input => {
          input.style.fontSize = '16px' // Prevents zoom on iOS
        })
      })
    },
    
    applyAndroidStyles() {
      // Android WebView already sits above the system nav — do not add inset again
      // (env(safe-area-inset-bottom) often double-counts and leaves a huge empty gap)
      document.documentElement.style.setProperty('--safe-area-top', '0px')
      document.documentElement.style.setProperty('--safe-area-bottom', '0px')
      document.documentElement.style.setProperty('--safe-area-left', '0px')
      document.documentElement.style.setProperty('--safe-area-right', '0px')
      
      // Android-specific behaviors (skip bottom nav — CSS handles press feedback)
      this.$nextTick(() => {
        const touchElements = document.querySelectorAll('.action-card, .feature-card')
        touchElements.forEach(element => {
          element.addEventListener('touchstart', this.handleTouchStart)
          element.addEventListener('touchend', this.handleTouchEnd)
        })
      })
    },
    
    handleTouchStart(event) {
      const el = event.currentTarget || event.target
      if (!el || !el.style) return
      el.style.transform = 'scale(0.97)'
      el.style.transition = 'transform 0.15s ease'
    },
    
    handleTouchEnd(event) {
      const el = event.currentTarget || event.target
      if (!el || !el.style) return
      el.style.transform = 'scale(1)'
    },
    
    goToNotifications() {
      this.$router.push('/notifications')
    },
    
    goToLogin() {
      this.$router.push('/login')
    },
    
    // Native Bridge Settings Button Control
    async showSettingsButton() {
      try {
        await nativeBridge.showSettingsButton()
      } catch (error) {
        console.error('Error showing settings button:', error)
      }
    },
    
    async hideSettingsButton() {
      try {
        await nativeBridge.hideSettingsButton()
      } catch (error) {
        console.error('Error hiding settings button:', error)
      }
    },
    
    // Auto-control settings button based on current page
    async controlSettingsButtonVisibility() {
      const currentPath = this.$route.path
      const hideOnPaths = [
        '/login',
        '/welcome',
        '/auth',
        '/register',
        '/'
      ]
      
      if (hideOnPaths.some(path => currentPath.includes(path))) {
        await this.hideSettingsButton()
      } else {
        await this.showSettingsButton()
      }
    },
    
    handleAuthCommand(command) {
      switch (command) {
        case 'profile':
          this.$router.push('/profile')
          break
        case 'changePassword':
          this.$router.push('/change-password')
          break
        case 'nativeSettings':
          this.openNativeSettings()
          break
        case 'logout':
          this.handleLogout()
          break
      }
    },
    
    async handleLogout() {
      try {
        await this.logout()
        this.$router.push('/login')
        this.$message.success('Logged out successfully')
      } catch (error) {
        console.error('Logout error:', error)
        this.$message.error('Failed to logout')
      }
    },
    
    async loadNotifications() {
      if (this.isAuthenticated && this.user?.employeeId) {
        try {
          console.log('Loading notifications for employee:', this.user.employeeId)
          const response = await this.getUnreadCount(this.user.employeeId)
          console.log('Notification response:', response)
          console.log('Current unread count:', this.unreadCount)
        } catch (error) {
          console.error('Failed to load notifications:', error)
        }
      } else {
        console.log('Not loading notifications - not authenticated or no employeeId')
        console.log('isAuthenticated:', this.isAuthenticated)
        console.log('user:', this.user)
      }
    },
    
    startNotificationPolling() {
      setTimeout(() => {
        this.loadNotifications()
      }, 1000)
      
      this.notificationInterval = setInterval(() => {
        this.loadNotifications()
      }, 30000) // Poll every 30 seconds
    },
    
    stopNotificationPolling() {
      if (this.notificationInterval) {
        clearInterval(this.notificationInterval)
        this.notificationInterval = null
      }
    },
    
    async handleLanguageChange(language) {
      try {
        await this.setLanguage(language)
        // Language changed successfully - no toast message needed
      } catch (error) {
        console.error('Failed to change language:', error)
        this.$message.error('Failed to change language')
      }
    },
    
    async refreshTranslations() {
      try {
        await this.$store.dispatch('language/refreshTranslations', 1) // Platform 1 for Vue.js web app
        this.$message.success('Translations refreshed successfully')
      } catch (error) {
        console.error('Failed to refresh translations:', error)
        this.$message.error('Failed to refresh translations')
      }
    },
    
    async clearTranslationCache() {
      try {
        await this.$store.dispatch('language/clearTranslationCache')
        this.$message.success('Translation cache cleared successfully')
        // Reload translations after clearing cache
        await this.$store.dispatch('language/loadTranslations', 1)
      } catch (error) {
        console.error('Failed to clear translation cache:', error)
        this.$message.error('Failed to clear translation cache')
      }
    },
    
    getCurrentFlag() {
      return this.getFlag(this.currentLanguage)
    },
    
    getFlag(languageCode) {
      const flags = {
        'en': '🇺🇸',
        'lo': '🇱🇦',
        'th': '🇹🇭',
        'vn': '🇻🇳',
        'kh': '🇰🇭',
        'zh': '🇨🇳'
      }
      return flags[languageCode] || '🌐'
    },
    
    // Translation function with fallback
    t(key) {
      try {
        const translations = this.$store.getters['language/translations']
        const currentLang = this.currentLanguage || 'en'
        
        if (translations && translations[key] && translations[key][currentLang]) {
          return translations[key][currentLang]
        }
        
        // Fallback to key if translation not found
        return key
      } catch (error) {
        console.warn('Translation error:', error)
        return key
      }
    },

    formatTime(timestamp) {
      const date = new Date(timestamp);
      const now = new Date();
      const diffInSeconds = Math.floor((now - date) / 1000);

      if (diffInSeconds < 60) {
        return `${diffInSeconds}s`;
      } else if (diffInSeconds < 3600) {
        return `${Math.floor(diffInSeconds / 60)}m`;
      } else if (diffInSeconds < 86400) {
        return `${Math.floor(diffInSeconds / 3600)}h`;
      } else {
        return `${Math.floor(diffInSeconds / 86400)}d`;
      }
    },

    getNotificationColor(type) {
      switch (type) {
        case 'info':
          return '#409EFF'; // Blue
        case 'success':
          return '#67C23A'; // Green
        case 'warning':
          return '#E6A23C'; // Orange
        case 'error':
          return '#F56C6C'; // Red
        default:
          return '#606266'; // Gray
      }
    },

    getNotificationIcon(type) {
      switch (type) {
        case 'info':
          return 'InfoFilled';
        case 'success':
          return 'CircleCheckFilled';
        case 'warning':
          return 'WarningFilled';
        case 'error':
          return 'CircleCloseFilled';
        default:
          return 'InfoFilled';
      }
    },

    handleNotificationCommand(command) {
      if (command === 'viewAll') {
        this.$router.push('/notifications');
      }
    },

    handleNotificationClick(notification) {
      // Mark as read if unread
      if (!notification.isRead) {
        this.markAsRead(notification.id);
      }
      
      // Navigate based on notification category and type
      if (notification.category === 'Leave' || notification.moduleType === 'Leave') {
        this.$router.push('/leave');
      } else if (notification.category === 'Timesheet' || notification.moduleType === 'Timesheet' || 
                 notification.category === 'Attendance' || notification.moduleType === 'Attendance') {
        this.$router.push('/attendance');
      } else if (notification.category === 'Recruitment' || notification.moduleType === 'Recruitment') {
        this.$router.push('/recruitment');
      } else if (notification.category === 'KPI' || notification.moduleType === 'KPI' || 
                 notification.category === 'Performance' || notification.moduleType === 'Performance') {
        this.$router.push('/kpi/list');
      } else if (notification.category === 'OT' || notification.moduleType === 'OT' || 
                 notification.category === 'Overtime' || notification.moduleType === 'Overtime') {
        this.$router.push('/ot-management');
      } else if (notification.category === 'Employee' || notification.moduleType === 'Employee' || 
                 notification.category === 'Profile' || notification.moduleType === 'Profile') {
        this.$router.push('/profile');
      } else if (notification.type === 'system' || notification.type === 'System') {
        // For system notifications, just mark as read and stay on current page
        // No toast message shown
      } else {
        // For unknown types, navigate to notifications page
        this.$router.push('/notifications');
      }
    },

    async markAsRead(notificationId) {
      try {
        await this.$store.dispatch('notifications/markAsRead', notificationId);
        this.$message.success('Notification marked as read');
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
        this.$message.error('Failed to mark notification as read');
      }
    },

    async markAllAsRead() {
      try {
        await this.$store.dispatch('notifications/markAllAsRead');
        this.$message.success('All notifications marked as read');
      } catch (error) {
        console.error('Failed to mark all notifications as read:', error);
        this.$message.error('Failed to mark all notifications as read');
      }
    },

    async refreshNotifications() {
      this.refreshingNotifications = true;
      try {
        // Refresh notifications data
        if (this.user?.employeeId) {
          await this.$store.dispatch('notifications/getUnreadCount', this.user.employeeId);
          await this.$store.dispatch('notifications/getRecentNotifications', this.user.employeeId);
        }
        this.$message.success('Notifications refreshed successfully');
      } catch (error) {
        console.error('Failed to refresh notifications:', error);
        this.$message.error('Failed to refresh notifications');
      } finally {
        this.refreshingNotifications = false;
      }
    },

    async clearDataCache() {
      try {
        await this.$store.dispatch('storage/clearAllData');
        // Reload translations after clearing cache
        await this.$store.dispatch('language/loadTranslations', 1);
      } catch (error) {
        console.error('Failed to clear data cache:', error);
      }
    },

    async openNativeSettings() {
      try {
        console.log('🔧 openNativeSettings called');
        
        // Force check Flutter bridge first
        const isFlutter = nativeBridge.checkFlutterBridge();
        console.log('🔧 Flutter bridge check result:', isFlutter);
        console.log('🔧 isFlutter:', nativeBridge.isRunningInFlutter());
        console.log('🔧 window.flutterBridge:', !!window.flutterBridge);
        console.log('🔧 window.Flutter:', !!window.Flutter);
        
        const result = await nativeBridge.openNativeSettings();
        console.log('🔧 openNativeSettings result:', result);
        
        if (result) {
          this.$message.success('Opening native settings...');
        } else {
          this.$message.warning('Native settings not available in web browser');
        }
      } catch (error) {
        console.error('❌ Error opening native settings:', error);
        this.$message.error('Failed to open native settings: ' + error.message);
      }
    },

    async ensureUserProfileData() {
      try {
        const user = this.$store.getters['auth/user']
        if (!user || !user.employeeId) {
          console.log('❌ No user or employeeId available for profile data loading')
          return
        }
        
        console.log('🔄 Ensuring user profile data is loaded for employeeId:', user.employeeId)
        
        // Load essential profile data that other modules depend on
        await Promise.all([
          this.$store.dispatch('notifications/getUnreadCount', user.employeeId),
          this.$store.dispatch('leave/getLeaveTypes'),
          this.$store.dispatch('employee/mobileGetEmployeeProfile', user.employeeId)
        ])

        // Fill empCode (and keep alias) on auth user for header display
        const profile = this.$store.state.employee?.employeeProfile
        if (profile) {
          const empCode = (profile.empCode || profile.EmpCode || '').toString().trim()
          const aliasName = (user.aliasName || user.userFullName || '').toString().trim()
          if ((empCode && empCode !== user.empCode) || (!user.aliasName && aliasName)) {
            this.$store.commit('auth/SET_USER', {
              ...user,
              empCode: empCode || user.empCode || '',
              aliasName: aliasName || user.aliasName || ''
            })
          } else if (empCode && !user.empCode) {
            this.$store.commit('auth/SET_USER', {
              ...user,
              empCode
            })
          }
        }
        
        console.log('✅ User profile data loaded successfully')
      } catch (error) {
        console.error('❌ Error loading user profile data:', error)
      }
    }
  },
  async mounted() {
    console.log('🚀 App.vue mounted - starting optimized initialization')
    
    // Start time tracking
    const startTime = performance.now()
    
    try {
      // 1. Initialize Flutter bridge (fast, local operation)
      this.loadingMessage = 'Initializing Flutter bridge...'
      await this.$store.dispatch('initFlutterBridge')
      console.log('✅ Flutter bridge initialized')
      
      // 2. Initialize language store with caching optimization
      this.loadingMessage = 'Loading translations...'
      const languagePromise = this.$store.dispatch('language/initialize')
      
      // 3. Check authentication status in parallel
      this.loadingMessage = 'Checking authentication...'
      const authPromise = this.$store.dispatch('auth/checkAuthStatus')
      
      // 4. Wait for both core services to complete
      await Promise.all([languagePromise, authPromise])
      console.log('✅ Core services initialized')
      
      // 5. Initialize additional services only if authenticated
      if (this.isAuthenticated) {
        this.loadingMessage = 'Loading user data...'
        // Initialize storage and notifications in parallel
        const storagePromise = this.$store.dispatch('storage/initialize')
        const notificationPromise = this.loadNotifications()
        
        await Promise.all([storagePromise, notificationPromise])
        console.log('✅ Additional services initialized')
        
        // Start notification polling
        this.startNotificationPolling()
      }
      
      // 6. Start time update
      this.updateCurrentTime()
      setInterval(this.updateCurrentTime, 1000)

      // 7. Detect platform and apply styles
      this.detectPlatform()
      this.applyPlatformStyles()
      
      // 7.5. Check Flutter bridge if on mobile
      if (this.isMobile) {
        console.log('🔧 Checking Flutter bridge during initialization...');
        nativeBridge.checkFlutterBridge();
        
        // Set up Flutter message handler
        window.handleFlutterMessage = (message) => {
          console.log('Flutter message received:', message);
          try {
            const data = JSON.parse(message);
            if (data.action === 'camera' && data.result && data.result.success) {
              // Handle camera result
              console.log('Camera result received:', data.result);
              // Trigger profile image update if needed
              if (data.result.base64) {
                // This will be handled by the component that called the camera
                console.log('Base64 image received, length:', data.result.base64.length);
              }
            }
          } catch (e) {
            console.error('Error parsing Flutter message:', e);
          }
        };
      }
      
      // 8. Hide loading screen
      this.isLoading = false
      
      // 9. Control settings button visibility based on current route
      await this.controlSettingsButtonVisibility()

      this.$nextTick(() => {
        this.navIndicatorReady = true
      })
      
      const endTime = performance.now()
      console.log(`✅ App initialization completed in ${(endTime - startTime).toFixed(2)}ms`)
      
    } catch (error) {
      console.error('❌ App initialization failed:', error)
      // Continue app loading even if some services fail
      
      // Still start time update even if other services fail
      this.updateCurrentTime()
      setInterval(this.updateCurrentTime, 1000)

      // Hide loading screen even if there are errors
      this.isLoading = false
    }
  },
  beforeUnmount() {
    this.stopNotificationPolling()
  },
  watch: {
    isAuthenticated(newVal) {
      if (newVal) {
        console.log('🔐 User authenticated, initializing services...')
        // Initialize storage only after authentication
        this.$store.dispatch('storage/initialize')
        this.loadNotifications()
        this.startNotificationPolling()
        
        // Ensure user profile data is available
        this.ensureUserProfileData()
      } else {
        console.log('🔓 User not authenticated, stopping services...')
        this.stopNotificationPolling()
      }
    },
    
    // Watch route changes to control settings button visibility
    '$route'(to, from) {
      this.controlSettingsButtonVisibility()
    }
  }
}
</script>

<style>
#app {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8f9fa;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  background-color: #f8f9fa;
  margin: 0;
  padding: 0;
}

/* Loading Screen */
.loading-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.loading-content {
  text-align: center;
  color: white;
  padding: 40px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.loading-logo {
  height: 80px;
  width: auto;
  max-width: 250px;
  object-fit: contain;
  margin-bottom: 30px;
  filter: brightness(0) invert(1);
}

.loading-spinner {
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin: 0 auto 30px auto;
}

.loading-text {
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 15px;
  color: white;
}

.loading-subtext {
  font-size: 18px;
  color: rgba(255, 255, 255, 0.8);
}

/* Mobile Header - Updated for better mobile fit */
.mobile-header {
  background: #1c75bc !important;
  color: #fff !important;
  padding: 6px 12px;
  box-shadow: 0 1px 4px rgba(28,117,188,0.08);
  position: sticky;
  top: 0;
  z-index: 1000;
  border-bottom: 1px solid #808285;
  min-height: 40px !important;
  padding-top: 2px !important;
  padding-bottom: 2px !important;
}

/* Remove forced background for all header children */
.mobile-header * {
  background: transparent !important;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  gap: 8px;
}

.app-logo {
  height: 28px;
  width: auto;
  max-width: 100px;
  object-fit: contain;
  display: block;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

/* Header Navigation - Use Element Plus Standard CSS Only */
.language-selector {
  display: flex;
  align-items: center;
  gap: 4px;
}

.notification-section {
  display: flex;
  align-items: center;
  gap: 4px;
}

.notification-badge {
  cursor: pointer;
}

.auth-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.user-menu-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 140px;
  padding: 2px 4px 2px 2px;
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
}

.user-menu-name {
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 88px;
}

.user-menu-caret {
  font-size: 12px;
  opacity: 0.85;
}

.user-menu-header {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
  padding: 2px 0;
}

.user-menu-header strong {
  font-size: 13px;
  color: #303133;
}

.user-menu-header-item {
  cursor: default !important;
  opacity: 1 !important;
}

.user-menu-header-item:hover {
  background: transparent !important;
}

@media (max-width: 480px) {
  .user-menu-name {
    display: none;
  }
  .user-menu-trigger {
    max-width: none;
  }
}

/* Platform-specific styles */
.platform-android {
  --safe-area-top: 0px;
  --safe-area-bottom: 0px;
  --safe-area-left: 0px;
  --safe-area-right: 0px;
  --touch-feedback: scale(0.95);
  --nav-height: 56px;
}

.platform-ios {
  --safe-area-top: env(safe-area-inset-top, 0px);
  /* Cap home-indicator inset so nav does not grow a tall empty strip */
  --safe-area-bottom: min(12px, env(safe-area-inset-bottom, 0px));
  --safe-area-left: env(safe-area-inset-left, 0px);
  --safe-area-right: env(safe-area-inset-right, 0px);
  --touch-feedback: scale(0.98);
  --nav-height: 56px;
}

.platform-web {
  --safe-area-top: 0px;
  --safe-area-bottom: 0px;
  --safe-area-left: 0px;
  --safe-area-right: 0px;
  --touch-feedback: scale(1);
  --nav-height: 58px;
}

/* Mobile-specific styles */
.mobile {
  --font-size-base: 14px;
  --font-size-large: 16px;
  --font-size-small: 12px;
  --spacing-base: 12px;
  --border-radius: 8px;
}

.desktop {
  --font-size-base: 16px;
  --font-size-large: 18px;
  --font-size-small: 14px;
  --spacing-base: 16px;
  --border-radius: 12px;
}

/* Android-specific touch feedback */
.platform-android .action-card:active,
.platform-android .feature-card:active {
  transform: var(--touch-feedback);
  transition: transform 0.1s ease;
}

/* iOS-specific input handling */
.platform-ios input,
.platform-ios textarea,
.platform-ios select {
  font-size: 16px !important; /* Prevents zoom on iOS */
}

/* Mobile navigation — compact bar + full-tab sliding highlight */
.mobile .mobile-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: auto;
  min-height: 0;
  /* Tight bottom padding — Android has no extra safe-area; iOS capped via CSS var */
  padding: 3px 2px calc(2px + var(--safe-area-bottom, 0px)) 2px;
  background: rgba(255, 255, 255, 0.96) !important;
  border-top: 1px solid rgba(0, 0, 0, 0.06) !important;
  box-shadow: 0 -6px 18px rgba(0, 0, 0, 0.08) !important;
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  display: flex;
  align-items: stretch;
  box-sizing: border-box;
}

.mobile .mobile-nav .nav-track {
  position: relative;
  display: flex;
  align-items: stretch;
  width: 100%;
  height: var(--nav-height, 56px);
  min-height: var(--nav-height, 56px);
}

.mobile .mobile-nav .nav-indicator {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
  height: 100%;
  pointer-events: none;
  opacity: 0;
  will-change: transform;
  box-sizing: border-box;
  padding: 1px;
}

.mobile .mobile-nav .nav-indicator-pill {
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  margin: 0;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-bottom-color: rgba(10, 55, 110, 0.35);
  background:
    linear-gradient(
      165deg,
      rgba(255, 255, 255, 0.55) 0%,
      rgba(255, 255, 255, 0.12) 28%,
      rgba(255, 255, 255, 0) 48%
    ),
    linear-gradient(180deg, #5eb0ef 0%, #2b8ad4 38%, #1c75bc 72%, #145a96 100%);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.55) inset,
    0 -3px 8px rgba(8, 48, 90, 0.28) inset,
    0 4px 10px rgba(28, 117, 188, 0.38),
    0 8px 18px rgba(20, 90, 150, 0.22);
}

.mobile .mobile-nav .nav-crystal-shine {
  position: absolute;
  top: 3px;
  left: 10%;
  right: 10%;
  height: 38%;
  border-radius: 999px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.72) 0%,
    rgba(255, 255, 255, 0.18) 55%,
    rgba(255, 255, 255, 0) 100%
  );
  pointer-events: none;
}

.mobile .mobile-nav .nav-crystal-glow {
  position: absolute;
  left: 18%;
  right: 18%;
  bottom: 4px;
  height: 28%;
  border-radius: 999px;
  background: radial-gradient(
    ellipse at center,
    rgba(120, 200, 255, 0.45) 0%,
    rgba(28, 117, 188, 0) 70%
  );
  filter: blur(1px);
  pointer-events: none;
}

.mobile .mobile-nav .nav-indicator.is-ready {
  opacity: 1;
  transition:
    transform 0.34s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.2s ease;
}

.mobile .mobile-nav .nav-indicator.is-ready .nav-indicator-pill {
  animation: nav-crystal-pulse 2.4s ease-in-out infinite;
}

@keyframes nav-crystal-pulse {
  0%, 100% {
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.55) inset,
      0 -3px 8px rgba(8, 48, 90, 0.28) inset,
      0 4px 10px rgba(28, 117, 188, 0.38),
      0 8px 18px rgba(20, 90, 150, 0.22);
  }
  50% {
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.65) inset,
      0 -3px 8px rgba(8, 48, 90, 0.28) inset,
      0 5px 14px rgba(28, 117, 188, 0.48),
      0 10px 22px rgba(20, 90, 150, 0.28);
  }
}

.mobile .main-content {
  padding-bottom: calc(var(--nav-height, 56px) + 16px + var(--safe-area-bottom, 0px));
  width: 100vw;
  overflow-x: hidden;
}

/* Platform-specific header adjustments - Updated for full screen */
.platform-ios .mobile-header {
  padding-top: calc(8px + var(--safe-area-top));
}

.platform-android .mobile-header {
  padding-top: 8px;
}

/* Touch-friendly button sizes for mobile */
.mobile .el-button {
  min-height: 44px; /* iOS recommended touch target size */
  min-width: 44px;
}

.mobile .nav-item {
  flex: 1 1 0;
  min-width: 0;
  height: 100%;
  margin: 0;
  padding: 4px 1px;
  border: none;
  outline: none;
  background: transparent !important;
  color: #808285 !important;
  cursor: pointer;
  font: inherit;
  font-weight: 500;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.25s ease;
  box-sizing: border-box;
}

.mobile .nav-item-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 100%;
  height: 100%;
  transform: translateZ(0);
  transition: transform 0.22s cubic-bezier(0.22, 1, 0.36, 1);
}

.mobile .nav-item .nav-icon,
.mobile .nav-item .nav-label {
  color: inherit !important;
  transition:
    color 0.25s ease,
    transform 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.25s ease;
}

.mobile .nav-item .nav-icon {
  font-size: 20px;
  line-height: 1;
  display: flex;
  transform: scale(1);
}

.mobile .nav-item .nav-label {
  font-size: 10px;
  line-height: 1.15;
  white-space: nowrap;
  opacity: 0.9;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mobile .nav-item.active {
  color: #fff !important;
  background: transparent !important;
  box-shadow: none !important;
}

.mobile .nav-item.active .nav-icon {
  transform: scale(1.1);
  filter: drop-shadow(0 1px 1px rgba(0, 40, 80, 0.35));
}

.mobile .nav-item.active .nav-label {
  opacity: 1;
  font-weight: 700;
  text-shadow: 0 1px 1px rgba(0, 40, 80, 0.28);
}

.mobile .nav-item:active .nav-item-inner {
  transform: scale(0.94);
}

.mobile .nav-item:focus,
.mobile .nav-item:focus-visible,
.mobile .nav-item:focus-within {
  outline: none !important;
  background: transparent !important;
  box-shadow: none !important;
}

/* Keep platform bars compact — do not inflate height */
.platform-android .mobile-nav,
.platform-ios .mobile-nav {
  height: auto;
  min-height: 0;
  padding: 3px 2px calc(2px + var(--safe-area-bottom, 0px)) 2px;
}

/* Android: cancel body safe-area padding from index.html (double gap) */
.platform-android body,
body.platform-android,
html.platform-android body,
body.mobile.platform-android {
  padding-bottom: 0 !important;
}

/* Classes are on body via applyPlatformStyles */
body.platform-android {
  padding-bottom: 0 !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

/* Main content padding for compact nav */
.main-content, .mobile-content {
  padding-bottom: calc(var(--nav-height, 56px)  + 16px + var(--safe-area-bottom, 0px));
}
.platform-android .main-content, .platform-android .mobile-content {
  padding-bottom: calc(var(--nav-height, 56px)  + 16px + var(--safe-area-bottom, 0px));
}
.platform-ios .main-content, .platform-ios .mobile-content {
  padding-bottom: calc(var(--nav-height, 56px)  + 16px + var(--safe-area-bottom, 0px));
}

.platform-ios .main-content,
.platform-ios .mobile-content,
.platform-android .main-content,
.platform-android .mobile-content {
  padding-bottom: calc(var(--nav-height, 56px)  + 16px + var(--safe-area-bottom, 0px)) !important;
}

/* Platform-specific loading screen */
.platform-indicator {
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 5px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: bold;
  z-index: 1001;
}

/* Mobile content adjustments */
.mobile-content {
  font-size: var(--font-size-base);
  width: 100vw;
  overflow-x: hidden;
  max-width: 100vw;
  padding-bottom: calc(var(--nav-height, 56px)  + 16px + var(--safe-area-bottom, 0px));
}

.platform-android .mobile-content {
  padding-bottom: calc(var(--nav-height, 56px)  + 16px + var(--safe-area-bottom, 0px));
}

.platform-ios .mobile-content {
  padding-bottom: calc(var(--nav-height, 56px)  + 16px + var(--safe-area-bottom, 0px));
}

.mobile-content .el-card {
  border-radius: var(--border-radius);
  margin: var(--spacing-base);
  max-width: calc(100vw - 2 * var(--spacing-base));
}

.mobile-content .el-form-item {
  margin-bottom: var(--spacing-base);
}

/* Ensure content fits within viewport */
.mobile-content .el-container,
.mobile-content .el-main,
.mobile-content .el-aside {
  max-width: 100vw;
  overflow-x: hidden;
}

/* Improve table responsiveness */
.mobile-content .el-table {
  font-size: 12px;
}

.mobile-content .el-table th,
.mobile-content .el-table td {
  padding: 8px 4px;
}

/* Improve form responsiveness */
.mobile-content .el-form {
  max-width: 100%;
}

.mobile-content .el-input,
.mobile-content .el-select {
  font-size: 14px;
}

/* Improve button sizing for mobile */
.mobile-content .el-button {
  min-height: 40px;
  font-size: 14px;
}

/* Improve dialog sizing for mobile */
.mobile-content .el-dialog {
  max-width: 90vw;
  margin: 5vh auto;
}

/* Improve dropdown sizing for mobile */
.mobile-content .el-dropdown-menu {
  max-width: 90vw;
}

/* Responsive adjustments for different screen sizes */
@media (max-width: 480px) {
  .mobile .nav-item .nav-icon {
    font-size: 17px;
  }
  
  .mobile .nav-item .nav-label {
    font-size: 9px;
  }
}

@media (max-width: 360px) {
  .mobile .mobile-nav {
    padding-left: 4px;
    padding-right: 4px;
  }
  
  .mobile .nav-item {
    padding: 2px 1px;
  }
  
  .mobile .nav-item .nav-label {
    font-size: 8px;
  }
}

/* Override Element Plus link button defaults for header */
.el-button--text {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  color: white !important;
}

.el-button--text:hover {
  background: rgba(255, 255, 255, 0.1) !important;
  border: none !important;
  box-shadow: none !important;
}

.el-button--text:focus {
  background: rgba(255, 255, 255, 0.1) !important;
  border: none !important;
  box-shadow: none !important;
}

.auth-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Usable, modern header buttons for navigation */
.language-selector button,
.notification-section button,
.auth-actions button {
  background: #808285;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 6px 12px;
  min-width: 36px;
  min-height: 36px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
  font: inherit;
  font-weight: 500;
  font-size: 15px;
  transition: background 0.15s;
}

.language-selector button:focus,
.language-selector button:hover,
.notification-section button:focus,
.notification-section button:hover,
.auth-actions button:focus,
.auth-actions button:hover {
  background: #1c75bc;
  color: #fff;
  outline: none;
}

.language-selector button .el-icon,
.notification-section button .el-icon,
.auth-actions button .el-icon {
  font-size: 18px;
}

.auth-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Classic style for notifications dropdown */
.notifications-dropdown {
  background: #fff !important;
  border: 1px solid #808285 !important;
  border-radius: 8px !important;
  box-shadow: 0 2px 8px rgba(28,117,188,0.08) !important;
  padding: 0 !important;
  min-width: 320px !important;
  max-width: 400px !important;
}

.notifications-header {
  background: #1c75bc !important;
  color: #fff !important;
  padding: 8px 16px !important;
  border-bottom: 1px solid #808285 !important;
  border-radius: 8px 8px 0 0 !important;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  min-width: 320px;
}

.notifications-title {
  font-weight: bold;
  font-size: 16px;
}

.notifications-list {
  background: #fff !important;
  padding: 0 !important;
}

.notification-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-bottom: 1px solid #eee;
  background: #fff !important;
  color: #333;
  cursor: pointer;
  transition: background 0.15s;
}

.notification-item.unread {
  background: #e6f0fa !important;
  border-left: 4px solid #1c75bc;
}

.notification-item:last-child {
  border-bottom: none;
}

.notification-icon {
  color: #1c75bc !important;
}

.notification-title {
  font-weight: 500;
  font-size: 14px;
  color: #1c75bc;
}

.notification-time {
  font-size: 12px;
  color: #808285;
}

.mark-all-read-btn,
.mark-read-btn {
  background: #808285;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.15s;
}

.mark-all-read-btn:hover,
.mark-read-btn:hover {
  background: #1c75bc;
}

.cache-age-indicator {
  font-size: 10px;
  color: #fff;
  opacity: 0.7;
  margin-left: 4px;
  min-width: 22px;
  display: inline-block;
  text-align: right;
}

.lang-code-small {
  font-size: 10px;
  font-weight: 600;
  margin-left: 2px;
  letter-spacing: 0.5px;
  color: #fff;
  opacity: 0.85;
}

.platform-ios .mobile-header,
.platform-android .mobile-header {
  background: #1c75bc !important;
  color: #fff !important;
}

.platform-ios .mobile-header *,
.platform-android .mobile-header * {
  color: #fff !important;
  background: transparent !important;
}

.platform-ios .mobile-nav,
.platform-android .mobile-nav {
  background: rgba(255, 255, 255, 0.94) !important;
  border-top: 1px solid rgba(0, 0, 0, 0.06) !important;
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.08) !important;
}

.platform-ios .mobile-header,
.platform-android .mobile-header,
.mobile-header {
  min-height: 40px !important;
  padding-top: 2px !important;
  padding-bottom: 2px !important;
}

/* Final bottom-nav polish (wins over older overrides) */
.mobile-nav .nav-item {
  -webkit-tap-highlight-color: transparent !important;
  background: transparent !important;
  color: #808285 !important;
}

.mobile-nav .nav-item.active {
  background: transparent !important;
  color: #fff !important;
  box-shadow: none !important;
}

.mobile-nav .nav-item.active .nav-icon,
.mobile-nav .nav-item.active .nav-label,
.mobile-nav .nav-item.active .el-icon {
  color: #fff !important;
}

.mobile-nav .nav-item:not(.active) .nav-icon,
.mobile-nav .nav-item:not(.active) .nav-label,
.mobile-nav .nav-item:not(.active) .el-icon {
  color: #808285 !important;
}

.mobile-nav .nav-item:focus,
.mobile-nav .nav-item:focus-visible,
.mobile-nav .nav-item:focus-within {
  outline: none !important;
  box-shadow: none !important;
  background: transparent !important;
}

.mobile-nav .nav-item:active:not(.active) {
  color: #808285 !important;
  background: transparent !important;
}

.mobile-nav .nav-item.active:active {
  color: #fff !important;
  background: transparent !important;
}

@media (prefers-reduced-motion: reduce) {
  .mobile .mobile-nav .nav-indicator.is-ready,
  .mobile .mobile-nav .nav-indicator.is-ready .nav-indicator-pill,
  .mobile .nav-item,
  .mobile .nav-item-inner,
  .mobile .nav-item .nav-icon,
  .mobile .nav-item .nav-label {
    transition: none !important;
    animation: none !important;
  }
}
</style> 
